import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Plus, Edit3, Trash2, Eye } from "lucide-react";
import { Country, State } from "country-state-city";
import { LoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

import Input from "../../components/Input";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import DataTable from "../../components/DataTable";
import { api } from "../../api/client";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";
const libraries = ["places"];

const numberOrEmpty = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

// Helper function to get photo data URL from base64
const getPhotoDataUrl = (photo, useThumbnail = false) => {
  if (!photo) {
    console.log("getPhotoDataUrl: photo is null/undefined");
    return null;
  }

  // Handle base64 stored photos
  if (photo.contentType) {
    // Use thumbnail if requested and available, otherwise use full data
    const imageData = useThumbnail && photo.thumbnail ? photo.thumbnail : photo.data;
    if (imageData) {
      // Thumbnails are always JPEG
      const contentType = useThumbnail && photo.thumbnail ? 'image/jpeg' : photo.contentType;
      const dataUrl = `data:${contentType};base64,${imageData}`;
      return dataUrl;
    } else {
      console.log("No image data available");
    }
  }

  // Fallback for old format (if any)
  if (typeof photo === 'string') {
    return photo;
  }

  return null;
};

// Google Places Autocomplete Component for Venue
function VenuePlacesAutocomplete({ value, onChange, onSelect }) {
  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      // Remove type restriction to show all places including addresses
    },
    debounce: 300,
  });

  const venueRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Sync external value with internal value
  useEffect(() => {
    if (value !== inputValue) {
      setValue(value, false);
    }
  }, [value, inputValue, setValue]);

  const handleInput = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange(newValue);
    setErrorMessage(""); // Clear error when typing
  };

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onSelect(address, { lat, lng });
      setErrorMessage("");
    } catch (error) {
      console.error("Error getting geocode:", error);
      onSelect(address, null);
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (venueRef.current && !venueRef.current.contains(event.target)) {
        clearSuggestions();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clearSuggestions]);

  // Check if API key is configured
  useEffect(() => {
    if (!ready && GOOGLE_MAPS_API_KEY === "YOUR_GOOGLE_MAPS_API_KEY") {
      setErrorMessage("⚠️ Google Maps API Key not configured. Please add your API key to .env file.");
    }
  }, [ready]);

  return (
    <div className="relative" ref={venueRef}>
      <input
        type="text"
        className={`w-full px-3 py-2 border ${errorMessage ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500`}
        value={inputValue}
        onChange={handleInput}
        disabled={!ready}
        autoComplete="off"
        placeholder={ready ? "Search for a venue or address..." : "Loading Google Places..."}
      />
      {errorMessage && (
        <div className="mt-1 text-xs text-red-600 flex items-start gap-1">
          <span>{errorMessage}</span>
        </div>
      )}
      {status === "OK" && data && data.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {data.map((suggestion) => (
            <div
              key={suggestion.place_id}
              onClick={() => handleSelect(suggestion.description)}
              className="px-3 py-2 cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 text-sm border-b border-gray-100 last:border-0"
            >
              <div className="font-medium text-gray-800">{suggestion.structured_formatting.main_text}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {suggestion.structured_formatting.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}
      {status === "ZERO_RESULTS" && inputValue && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
          <div className="text-xs text-gray-500">No venues found. Try a different search.</div>
        </div>
      )}
    </div>
  );
}

export default function EventsList() {
  const navigate = useNavigate();

  // auth (mirror of UserList pattern)
  const userLogin = useSelector((s) => s.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    if (!userInfo) navigate("/");
  }, [userInfo, navigate]);

  // Click outside handler to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);

  // photo viewer
  const [viewPhoto, setViewPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  // create modal
  const [openModal, setOpenModal] = useState(false);

  // Refs for click outside detection
  const locationRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    venue: "",
    location: "",
    category: "",
    startAt: "",
    basePrice: "",
    gstPercent: 18,
    discountedPrice: "",
    seatsTotal: "",
    seatsAvailable: "",
    coupons: [], // Array of {code, type, amount, isActive}
  });
  const [venueCoordinates, setVenueCoordinates] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  // Autocomplete states for location (country, state)
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  const loadCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(Array.isArray(res) ? res : res.data || []);
    } catch (e) {
      console.error("Failed to load categories:", e);
      setCategories([]);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const q = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await api.get(`/events${q}`);
      // supports controller returning {items:[], ...} or [] directly
      setRows(Array.isArray(res) ? res : res.items || []);
    } catch (e) {
      toast(e.message || "Failed to load events", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCategories();
  }, []); // first load

  // search on typing (small debounce)
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]);

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      venue: "",
      location: "",
      category: "",
      startAt: "",
      basePrice: "",
      gstPercent: 18,
      discountedPrice: "",
      seatsTotal: "",
      seatsAvailable: "",
      coupons: [],
    });
    setVenueCoordinates(null);
    setPhotoFile(null);
    setPhotoPreview("");
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  // Handle venue selection from Google Places
  const handleVenueSelect = (address, coordinates) => {
    setForm({ ...form, venue: address });
    setVenueCoordinates(coordinates);
  };

  // Handle location input change with autocomplete (Country, State)
  const handleLocationChange = (value) => {
    setForm({ ...form, location: value });

    if (value.trim().length > 0) {
      const suggestions = [];

      // Get all countries
      const allCountries = Country.getAllCountries();
      const filteredCountries = allCountries
        .filter(country =>
          country.name.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 5)
        .map(country => ({
          type: 'country',
          name: country.name,
          display: country.name,
          isoCode: country.isoCode
        }));

      // Get all states from all countries
      allCountries.forEach(country => {
        const states = State.getStatesOfCountry(country.isoCode);
        const filteredStates = states
          .filter(state =>
            state.name.toLowerCase().includes(value.toLowerCase())
          )
          .slice(0, 3)
          .map(state => ({
            type: 'state',
            name: state.name,
            display: `${state.name}, ${country.name}`,
            countryName: country.name,
            isoCode: state.isoCode
          }));
        suggestions.push(...filteredStates);
      });

      suggestions.push(...filteredCountries);

      setLocationSuggestions(suggestions.slice(0, 10));
      setShowLocationSuggestions(suggestions.length > 0);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  // Select location from suggestions
  const selectLocation = (location) => {
    setForm({ ...form, location: location.display });
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast("Please upload a valid image (JPG, PNG, or WebP)", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast("Image size should be less than 5MB", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
      return;
    }

    // Store file and create preview
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const closeCreate = () => {
    setOpenModal(false);
    resetForm();
  };

  const saveCreate = async () => {
    try {
      // Validate required fields with proper checks
      if (!form.name?.trim()) {
        throw new Error("Event name is required");
      }
      if (!form.venue?.trim()) {
        throw new Error("Venue is required");
      }
      if (!form.location?.trim()) {
        throw new Error("Location is required");
      }
      if (!form.startAt) {
        throw new Error("Start date/time is required");
      }
      if (form.basePrice === "" || form.basePrice === null || form.basePrice === undefined) {
        throw new Error("Base price is required");
      }
      if (form.gstPercent === "" || form.gstPercent === null || form.gstPercent === undefined) {
        throw new Error("GST percent is required");
      }
      if (form.seatsTotal === "" || form.seatsTotal === null || form.seatsTotal === undefined) {
        throw new Error("Total seats is required");
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("description", form.description || "");
      formData.append("venue", form.venue.trim());
      formData.append("location", form.location.trim());

      // Add coordinates if available
      if (venueCoordinates) {
        formData.append("latitude", String(venueCoordinates.lat));
        formData.append("longitude", String(venueCoordinates.lng));
      }

      if (form.category) {
        formData.append("category", form.category);
      }
      formData.append("startAt", new Date(form.startAt).toISOString());
      formData.append("basePrice", String(form.basePrice));
      formData.append("gstPercent", String(form.gstPercent));
      formData.append("discountedPrice", String(form.discountedPrice || 0));
      formData.append("seatsTotal", String(form.seatsTotal));

      // Auto-set seatsAvailable to seatsTotal if empty
      const availableSeats = form.seatsAvailable === "" || form.seatsAvailable === null || form.seatsAvailable === undefined
        ? form.seatsTotal
        : form.seatsAvailable;
      formData.append("seatsAvailable", String(availableSeats));

      // Add coupons array as JSON string
      if (form.coupons && form.coupons.length > 0) {
        formData.append("coupons", JSON.stringify(form.coupons));
      }

      if (photoFile) {
        formData.append("photo", photoFile);
      }

      await api.post("/events", formData);

      toast("Event created", {
        type: "success",
        autoClose: 1500,
        hideProgressBar: true,
      });
      closeCreate();
      load();
    } catch (e) {
      toast(e.message || "Failed to create event", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${id}`);
      toast("Deleted", {
        type: "success",
        autoClose: 1200,
        hideProgressBar: true,
      });
      load();
    } catch (e) {
      toast(e.message || "Delete failed", {
        type: "error",
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Event",
        cell: ({ row }) => (
          <Link
            className="font-bold text-md cursor-pointer text-green-800 bg-green-100 px-3 py-1 rounded inline-block hover:bg-green-200 transition-colors"
            to={`/events/${row.original._id}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      { accessorKey: "location", header: "City" },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) =>
          row.original.category?.name || (
            <span className="text-gray-400 text-xs">No category</span>
          ),
      },
      {
        accessorKey: "startAt",
        header: "Start",
        cell: ({ row }) =>
          row.original.startAt
            ? new Date(row.original.startAt).toLocaleString()
            : "",
      },
      {
        accessorKey: "pricing",
        header: "Price",
        cell: ({ row }) => {
          // full calculation but show only final price
          const b = Number(row.original.basePrice || 0);
          const g = Number(row.original.gstPercent || 0);
          const d = Number(row.original.discountedPrice || 0);
          const final = Math.max(0, b + (b * g) / 100 - d);

          return (
            <span className="font-semibold">
              ₹{final.toLocaleString("en-IN")}
            </span>
          );
        },
      },
      {
        accessorKey: "seats",
        header: "Seats",
        cell: ({ row }) =>
          `${row.original.seatsAvailable}/${row.original.seatsTotal}`,
      },
      {
        id: "photo",
        header: "Photo",
        cell: ({ row }) => {
          const hasPhoto = row.original.photos && row.original.photos.length > 0;
          const thumbnailUrl = hasPhoto ? getPhotoDataUrl(row.original.photos[0], true) : null;
          return thumbnailUrl ? (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                // Load full image from detail endpoint when viewing
                setPhotoLoading(true);
                try {
                  const fullEvent = await api.get(`/events/${row.original._id}`);                
                  
                  const fullPhotoUrl = fullEvent.photos && fullEvent.photos.length > 0
                    ? getPhotoDataUrl(fullEvent.photos[0], false)
                    : thumbnailUrl;
                  setViewPhoto(fullPhotoUrl);
                } catch (err) {
                  console.error("Error loading full image:", err);
                  // Fallback to thumbnail if loading fails
                  setViewPhoto(thumbnailUrl);
                } finally {
                  setPhotoLoading(false);
                }
              }}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1"
              title="View Photo"
            >
              <Eye size={14} />
              View
            </button>
          ) : (
            <span className="text-gray-400 text-xs">No photo</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/events/${row.original._id}`);
              }}
              className="bg-emerald-500 hover:bg-emerald-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1"
              title="Edit"
            >
              <Edit3 size={14} />
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                remove(row.original._id);
              }}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs flex items-center gap-1"
              title="Delete"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [navigate]
  );

  if (loading) return <Loading />;

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
      <div className="w-full h-full">
        <h1 className="text-2xl font-semibold mb-4">Events</h1>

      <DataTable
        data={rows}
        columns={columns}
        title="Event Management"
        isLoading={loading}
        toolbar={
          <>
            <Input
              width="w-64 mr-2"
              name="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name/city…"
            />
            <Button
              type="button"
              onClick={() => setOpenModal(true)}
              text={
                <span className="flex items-center gap-2">
                  <Plus size={16} />
                  Create Event
                </span>
              }
              className="bg-emerald-500 hover:bg-emerald-700"
            />
          </>
        }
      />

      {/* Create Event Modal */}
      {openModal && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="relative w-full max-w-4xl mx-auto my-6 bg-white rounded-lg shadow-lg max-h-[95vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold">Create Event</h3>
                <button
                  onClick={closeCreate}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
                >
                  &times;
                </button>
              </div>

              <div className="px-4 py-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Event Name */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Event Name *
                    </label>
                    <Input
                      width="w-full"
                      name="Name *"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                  </div>

                  {/* Venue with Google Places Autocomplete */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Venue (Detailed Address) *
                    </label>
                    <VenuePlacesAutocomplete
                      value={form.venue}
                      onChange={(value) => setForm({ ...form, venue: value })}
                      onSelect={handleVenueSelect}
                    />
                    <p className="text-xs text-gray-500 mt-1">Search for exact venue or address with Google Places</p>
                  </div>

                  {/* Location with Autocomplete (Country/State) */}
                  <div className="relative" ref={locationRef}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Location (Country/State) *
                    </label>
                    <Input
                      width="w-full"
                      name="Location *"
                      value={form.location}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      onFocus={() => {
                        if (form.location && locationSuggestions.length > 0) {
                          setShowLocationSuggestions(true);
                        }
                      }}
                      autoComplete="off"
                      placeholder="Type country or state..."
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {locationSuggestions.map((location, index) => (
                          <div
                            key={index}
                            onClick={() => selectLocation(location)}
                            className="px-3 py-2 cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 text-sm"
                          >
                            <div className="font-medium">{location.display}</div>
                            <div className="text-xs text-gray-500 capitalize">{location.type}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Select country or state (e.g., "California, United States" or "India")</p>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Category
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={form.category}
                      onChange={(e) =>
                        setForm({ ...form, category: e.target.value })
                      }
                    >
                      <option value="">-- Select Category --</option>
                      {categories
                        .filter((cat) => cat.isActive)
                        .map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Start Date &amp; Time *
                    </label>
                    <Input
                      width="w-full"
                      name="Start At *"
                      type="datetime-local"
                      value={form.startAt}
                      onChange={(e) =>
                        setForm({ ...form, startAt: e.target.value })
                      }
                    />
                  </div>

                  {/* Base Price */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Base Price (₹) *
                    </label>
                    <Input
                      width="w-full"
                      name="Base Price (₹) *"
                      type="number"
                      value={form.basePrice}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          basePrice: numberOrEmpty(e.target.value),
                        })
                      }
                    />
                  </div>

                  {/* GST */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      GST %
                    </label>
                    <Input
                      width="w-full"
                      name="GST % *"
                      type="number"
                      value={form.gstPercent}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          gstPercent: numberOrEmpty(e.target.value),
                        })
                      }
                    />
                  </div>

                  {/* Discount */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Discount Amount (₹)
                    </label>
                    <Input
                      width="w-full"
                      name="Discount Amount (₹)"
                      type="number"
                      value={form.discountedPrice}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          discountedPrice: numberOrEmpty(e.target.value),
                        })
                      }
                    />
                  </div>

                  {/* Coupons Section */}
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Coupons
                    </label>
                    <div className="space-y-2">
                      {form.coupons.map((coupon, index) => (
                        <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Input
                            width="w-32"
                            placeholder="CODE"
                            value={coupon.code}
                            onChange={(e) => {
                              const newCoupons = [...form.coupons];
                              newCoupons[index].code = e.target.value.toUpperCase();
                              setForm({ ...form, coupons: newCoupons });
                            }}
                          />
                          <select
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={coupon.type}
                            onChange={(e) => {
                              const newCoupons = [...form.coupons];
                              newCoupons[index].type = e.target.value;
                              setForm({ ...form, coupons: newCoupons });
                            }}
                          >
                            <option value="percent">Percent (%)</option>
                            <option value="flat">Flat (₹)</option>
                          </select>
                          <Input
                            width="w-24"
                            type="number"
                            placeholder="Amount"
                            value={coupon.amount}
                            onChange={(e) => {
                              const newCoupons = [...form.coupons];
                              newCoupons[index].amount = numberOrEmpty(e.target.value);
                              setForm({ ...form, coupons: newCoupons });
                            }}
                          />
                          <label className="flex items-center gap-1 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={coupon.isActive}
                              onChange={(e) => {
                                const newCoupons = [...form.coupons];
                                newCoupons[index].isActive = e.target.checked;
                                setForm({ ...form, coupons: newCoupons });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-gray-700">Active</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newCoupons = form.coupons.filter((_, i) => i !== index);
                              setForm({ ...form, coupons: newCoupons });
                            }}
                            className="ml-auto p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setForm({
                            ...form,
                            coupons: [
                              ...form.coupons,
                              { code: "", type: "percent", amount: "", isActive: true }
                            ]
                          });
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200"
                      >
                        <Plus size={16} />
                        Add Coupon
                      </button>
                    </div>
                  </div>

                  {/* Total Seats */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Total Seats *
                    </label>
                    <Input
                      width="w-full"
                      name="Total Seats *"
                      type="number"
                      value={form.seatsTotal}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          seatsTotal: numberOrEmpty(e.target.value),
                        })
                      }
                    />
                  </div>

                  {/* Available Seats */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Available Seats
                    </label>
                    <Input
                      width="w-full"
                      name="Available Seats"
                      type="number"
                      value={form.seatsAvailable}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          seatsAvailable: numberOrEmpty(e.target.value),
                        })
                      }
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Description
                    </label>
                    <Input
                      width="w-full"
                      name="Description"
                      value={form.description}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Event Photo
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                  />
                  {photoPreview && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-600 mb-2">Preview:</p>
                      <img
                        src={photoPreview}
                        alt="Event preview"
                        className="w-32 h-32 object-cover rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview("");
                        }}
                        className="mt-2 text-xs text-red-500 hover:text-red-700"
                      >
                        Remove photo
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  Fields with * are mandatory
                </p>
              </div>

              <div className="flex justify-end px-4 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="text-red-500 hover:text-red-700 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={saveCreate}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase text-sm px-6 py-2 rounded shadow"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Photo Viewer Modal */}
      {(viewPhoto || photoLoading) && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black opacity-50"
            onClick={() => {
              if (!photoLoading) {
                setViewPhoto(null);
              }
            }}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-lg max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold">
                  {photoLoading ? "Loading Photo..." : "Event Photo"}
                </h3>
                <button
                  onClick={() => {
                    if (!photoLoading) {
                      setViewPhoto(null);
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold disabled:opacity-50"
                  disabled={photoLoading}
                >
                  &times;
                </button>
              </div>
              <div className="p-4">
                {photoLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                  </div>
                ) : viewPhoto ? (
                  <img
                    src={viewPhoto}
                    alt="Event"
                    className="max-w-full max-h-[70vh] object-contain mx-auto"
                    onError={(e) => {
                      console.error("Image failed to load");
                      console.error("Image src:", e.target.src?.substring(0, 100));
                    }}
                    
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No photo available
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </LoadScript>
  );
}
